import type { PlaylistEntry } from '@/playback/PlaylistController'
import { audiobookAsin } from '@/playback/audible/audiobookAsin'

/**
 * Presentation classification for the transport / full-player control row.
 *
 * A video podcast is BOTH a `PodcastEpisode` and `video === true`, so the two
 * predicates deliberately overlap only on the podcast side: a video podcast is
 * a podcast (gets the 15/30 jump pair + playback-rate), NOT a VOD video (does
 * not also get the VOD 10/10 jump pair). VOD-only video is the non-podcast
 * `video === true` case. Keeping this here keeps Transport and FullPlayer from
 * drifting on the gate.
 */
export function isPodcastEntry(entry: PlaylistEntry | null | undefined): boolean {
    return entry?.entity.__typename === 'PodcastEpisode'
}

export function isVodVideoEntry(entry: PlaylistEntry | null | undefined): boolean {
    return entry?.video === true && !isPodcastEntry(entry)
}

/**
 * A standalone ASIN music track — the only audio kind the streaming-quality
 * gear applies to. Podcasts, audiobooks, and videos own their own settings; a
 * music-video Track carries `mediaType === 'VIDEO'`, so it routes to the video
 * gear rather than this one.
 */
export function isAsinTrackEntry(entry: PlaylistEntry | null | undefined): boolean {
    const entity = entry?.entity
    return entity?.__typename === 'Track' && entity.mediaType !== 'VIDEO'
}

/**
 * An audiobook entry — the transport steps chapters (sections) through the
 * player's section-nav surface for these instead of the single-entry feed
 * (GH parity). Detected by a resolvable audiobook asin, the same signal the
 * Audible engine activates on.
 */
export function isAudiobookEntry(entry: PlaylistEntry | null | undefined): boolean {
    return entry != null && audiobookAsin(entry) !== null
}
